import React, { useState, useEffect } from 'react';
import { Users, User, MessageSquare, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import Linkify from 'linkify-react';
const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const organizationTypes = ['Sunday School', 'MMVS', 'MGOCSM', 'Prayer Group'];

  useEffect(() => {
    fetchOrganizations();
  }, []);



  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/api/organizations');
      setOrganizations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationsByType = (type) => {
    return organizations.filter(org => org.organization_type === type);
  };

  const toggleExpanded = (orgId) => {
    setExpandedOrg(expandedOrg === orgId ? null : orgId);
  };

  const linkifyOptions = {
    attributes: {
      class: 'text-red-600 hover:text-red-800 underline break-all',
      target: '_blank',
      rel: 'noopener noreferrer'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Spiritual Organizations</h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            Discover our spiritual organizations and connect with like-minded individuals in your spiritual journey.
          </p>
        </div>
      </section>

      <section className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="space-y-12">
            {organizationTypes.map((type) => {
              const orgsOfType = getOrganizationsByType(type);
              
              return (
                <div key={type} id={type.toLowerCase().replace(/\s+/g, '-')} className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{type}</h3>
                    <div className="w-16 h-1 bg-red-600 mx-auto"></div>
                  </div>
                  
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                      <p className="text-gray-500 mt-3">Loading {type.toLowerCase()}...</p>
                    </div>
                  ) : orgsOfType.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <h4 className="text-lg font-medium text-gray-600 mb-2">No {type} data available</h4>
                      <p className="text-gray-500">No data has been added for this organization yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {orgsOfType.map((org) => (
                        <div
                          key={org._id}
                          className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                        >
                          <div
                            className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleExpanded(org._id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                                  <Users className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                  <h4 className="text-xl font-semibold text-gray-900">{org.title}</h4>
                                  <p className="text-gray-600 text-sm">Click to view details</p>
                                </div>
                              </div>
                              <ChevronRight
                                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                                  expandedOrg === org._id ? 'rotate-90' : ''
                                }`}
                              />
                            </div>
                          </div>

                          {expandedOrg === org._id && (
                            <div className="px-6 pb-6 border-t border-gray-200">
                              <div className="pt-6 space-y-4">
                                {org.description && (
                                  <div>
                                    <h5 className="font-semibold text-gray-900 mb-2">Description</h5>
                                    <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                                      <Linkify options={linkifyOptions}>
                                        {org.description}
                                      </Linkify>
                                    </p>
                                  </div>
                                )}

                                {org.members && (
                                  <div>
                                    <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                      <User className="w-4 h-4 mr-1" />
                                      Members
                                    </h5>
                                    <div className="text-gray-600 whitespace-pre-line">{org.members}</div>
                                  </div>
                                )}

                                {org.announcements && (
                                  <div>
                                    <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                      <MessageSquare className="w-4 h-4 mr-1" />
                                      Announcements
                                    </h5>
                                    <p className="text-gray-600 whitespace-pre-line">
                                      <Linkify options={linkifyOptions}>
                                        {org.announcements}
                                      </Linkify>
                                    </p>
                                  </div>
                                )}

                                {org.createdAt && (
                                  <div className="text-sm text-gray-500 pt-2 border-t border-gray-100">
                                    Created: {new Date(org.createdAt).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Organizations;
